
import { getValidToken } from "./tokenUtils";

export const fetchAttachment = async (messageId: string, attachmentId: string, email: string) => {
  try {
    console.log(`Fetching attachment: ${attachmentId} from message: ${messageId} for email: ${email}`);
    const token = getValidToken(email);
    
    if (!token) {
      console.error("No valid Gmail token found for email:", email);
      throw new Error("No valid Gmail token found");
    }
    
    const url = `https://www.googleapis.com/gmail/v1/users/me/messages/${messageId}/attachments/${attachmentId}`;
    console.log("Fetching from URL:", url);
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to fetch attachment: ${response.status}`, errorText);
      throw new Error(`Failed to fetch attachment: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log("Attachment data received, size:", data.size);
    
    if (!data.data) {
      console.error("No data field in attachment response:", data);
      throw new Error("Invalid attachment data received");
    }
    
    // Return the raw base64 data
    return data.data;
  } catch (error) {
    console.error("Error fetching attachment:", error);
    throw error;
  }
};

export const openAttachment = (attachment: { filename: string; mimeType: string }, base64Data: string) => {
  try {
    console.log(`Opening attachment: ${attachment.filename}, MIME type: ${attachment.mimeType}`);
    
    // Convert from base64url to standard base64
    // Replace URL-safe characters back to standard base64 characters
    let standardBase64 = base64Data.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    while (standardBase64.length % 4 !== 0) {
      standardBase64 += '=';
    }
    
    console.log(`Base64 data length: ${standardBase64.length}`);
    
    // Create a blob from the base64 data
    let byteCharacters;
    try {
      byteCharacters = atob(standardBase64);
      console.log("Successfully decoded base64 data");
    } catch (error) {
      console.error("Failed to decode base64:", error);
      throw new Error("Invalid base64 data: " + error.message);
    }
    
    const byteArrays = [];
    
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    
    const blob = new Blob(byteArrays, { type: attachment.mimeType });
    console.log(`Created blob of size: ${blob.size} bytes`);
    
    // Create object URL for the blob
    const blobUrl = URL.createObjectURL(blob);
    console.log("Created blob URL:", blobUrl);
    
    // For files that can be displayed in browser (images, PDFs, etc.)
    if (attachment.mimeType.startsWith('image/') || 
        attachment.mimeType === 'application/pdf' || 
        attachment.mimeType.startsWith('text/')) {
      console.log("Opening attachment in new tab:", attachment.filename);
      
      // Create a new window/tab with proper handling
      const newWindow = window.open('', '_blank');
      
      if (!newWindow) {
        console.error("Failed to open new window. Pop-up might be blocked.");
        alert("Failed to open the attachment. Please allow pop-ups for this site.");
        return false;
      }
      
      // Set content for the new window while it's loading
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${attachment.filename}</title>
            <meta charset="utf-8">
          </head>
          <body>
            <div style="text-align: center; margin-top: 20px;">
              <h2>Loading ${attachment.filename}...</h2>
            </div>
          </body>
        </html>
      `);
      
      // Let the loading message render, then set the actual content
      setTimeout(() => {
        if (newWindow.closed) {
          console.error("Window was closed before content could be loaded");
          return;
        }
        
        if (attachment.mimeType.startsWith('image/')) {
          newWindow.location.href = blobUrl;
        } else {
          // For PDF and text files, embed them in an appropriate element
          newWindow.document.body.innerHTML = '';
          if (attachment.mimeType === 'application/pdf') {
            newWindow.document.write(`
              <!DOCTYPE html>
              <html>
                <head>
                  <title>${attachment.filename}</title>
                  <meta charset="utf-8">
                  <style>
                    body, html { margin: 0; padding: 0; height: 100%; overflow: hidden; }
                  </style>
                </head>
                <body>
                  <embed src="${blobUrl}" type="application/pdf" width="100%" height="100%">
                </body>
              </html>
            `);
          } else {
            newWindow.location.href = blobUrl;
          }
        }
      }, 100);
      
      // Let the new window handle cleanup of the blob URL
      return true;
    } else {
      // For other files, trigger a download
      console.log("Creating download link for:", attachment.filename);
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.download = attachment.filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      // Clean up the object URL after a delay to ensure the download starts
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        console.log("Revoked blob URL for:", attachment.filename);
      }, 10000); // Extended timeout to ensure download has time to start
    }
    
    return true;
  } catch (error) {
    console.error("Error opening attachment:", error);
    throw error;
  }
};

// Helper function to download attachments directly
export const downloadAttachment = (attachment: { filename: string; mimeType: string }, base64Data: string) => {
  try {
    console.log(`Downloading attachment: ${attachment.filename}`);
    
    // Convert from base64url to standard base64
    let standardBase64 = base64Data.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    while (standardBase64.length % 4 !== 0) {
      standardBase64 += '=';
    }
    
    console.log(`Download base64 data length: ${standardBase64.length}`);
    
    // Create a blob from the base64 data
    let byteCharacters;
    try {
      byteCharacters = atob(standardBase64);
      console.log("Successfully decoded base64 data for download");
    } catch (error) {
      console.error("Failed to decode base64 for download:", error);
      alert("Failed to process attachment data. Please try again.");
      return false;
    }
    
    const byteArrays = [];
    
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    
    const blob = new Blob(byteArrays, { type: attachment.mimeType });
    console.log(`Created download blob of size: ${blob.size} bytes`);
    
    const blobUrl = URL.createObjectURL(blob);
    console.log("Created download blob URL:", blobUrl);
    
    // Create and trigger download link
    const downloadLink = document.createElement('a');
    downloadLink.href = blobUrl;
    downloadLink.download = attachment.filename;
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    
    // Attempt to download
    try {
      downloadLink.click();
      console.log("Download click triggered");
    } catch (error) {
      console.error("Error triggering download click:", error);
      return false;
    } finally {
      document.body.removeChild(downloadLink);
    }
    
    // Clean up the object URL after a longer delay to ensure download starts
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
      console.log("Revoked download blob URL for:", attachment.filename);
    }, 30000); // Extended timeout to ensure download has started
    
    return true;
  } catch (error) {
    console.error("Error downloading attachment:", error);
    return false;
  }
};
